/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { IMaskInput } from "react-imask";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { api, ApiError } from "core/api";
import { Button, Card, Input, Form, Select, Switch, FieldGroup } from "core/components";
import { navigateTo } from "core/navigation";
import { InventoryItemFormData, SupplierFormData, SupplierSchema, WarehouseFormData } from "core/schema";
import { admin_supplier_path, admin_suppliers_path } from "routes";
import SupplierItemsForm from "./SupplierItemsForm";

type SupplierFormProps = {
  supplier: SupplierFormData;
  supplier_items: Array<ItemSupplierFormData>;
  tax_categories: Record<string, string>;
  inventory_items: Array<InventoryItemFormData>;
  warehouses: Array<WarehouseFormData>;
  credit_terms: Array<Record<string, unknown>>;
};

enum SupplierFormTabs {
  Supplier = 0,
  Items = 1
}

const SupplierForm = ({ supplier, tax_categories, inventory_items, warehouses, credit_terms, accounts }: SupplierFormProps): JSX.Element => {
  const formMethods = useForm<SupplierFormData>({
    mode: "onChange",
    defaultValues: SupplierSchema.cast(supplier),
    resolver: yupResolver(SupplierSchema)
  });
  const {
    control,
    register,
    handleSubmit,
    formState: { isSubmitting, errors }
  } = formMethods;
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);
  const [selectedTab, setSelectedTab] = React.useState(SupplierFormTabs.Supplier);

  const save = async (data: SupplierFormData): Promise<unknown> => {
    try {
      if (data.id) {
        await api.put(admin_supplier_path(data), data);
      } else {
        await api.post(admin_suppliers_path(), data);
      }

      navigateTo(admin_suppliers_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  return (
    <React.StrictMode>
      <FormProvider {...formMethods}>
        <Form errors={formErrors}>
          <Tabs
            className="card"
            selectedTabClassName="project-tab__link project-tab__link--active"
            onSelect={(index) => {
              setSelectedTab(index);
            }}
          >
            <Container fluid={true}>
              <Row className="d-flex align-items-baseline">
                <Col>
                  <TabList className="card__header" style={{ padding: 0 }}>
                    <Col className="d-flex justify-content-start">
                      <Tab className="project-tab__link">Supplier</Tab>
                      {supplier.id && <Tab className="project-tab__link">Items</Tab>}
                    </Col>
                  </TabList>
                </Col>
                <Col className="d-flex justify-content-end">
                  <Button
                    icon="arrow-left"
                    iconProps={{ className: "mr-3" }}
                    className="button--default mr-3"
                    disabled={isSubmitting}
                    title="Back"
                    onClick={() => navigateTo(admin_suppliers_path())}
                  />
                  {selectedTab == SupplierFormTabs.Supplier && (
                    <Button
                      icon="save"
                      iconProps={{ className: "mr-3" }}
                      className="button--brand mr-3"
                      disabled={isSubmitting}
                      title="Save"
                      onClick={handleSubmit(async (data) => await save(data))}
                    />
                  )}
                </Col>
              </Row>
            </Container>
            <TabPanel>
              <Card.Body>
                <Container fluid={true}>
                  <Row className="px-3">
                    <Col>
                      <Controller
                        name="active"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            {...field}
                            label="Active"
                            info="Active or Inactive"
                            checked={field.value}
                            value={field.value?.toString()}
                            errorMessage={errors.active?.message}
                          />
                        )}
                      />
                    </Col>
                  </Row>
                  <Row className="px-3">
                    <Col>
                      <Controller
                        name="code"
                        control={control}
                        render={({ field }) => (
                          <FieldGroup label="Code" required errorMessage={errors.code?.message}>
                            <IMaskInput
                              mask="V00000"
                              value={field.value}
                              unmask={false}
                              inputRef={field.ref}
                              placeholder="V00001"
                              onAccept={(value) => {
                                field.onChange(value);
                              }}
                              className="field text-uppercase"
                            />
                          </FieldGroup>
                        )}
                      />
                      <Input
                        {...register("name")}
                        label="Name"
                        required
                        placeholder="Wilcon Depot, Inc."
                        errorMessage={errors.name?.message}
                        maxLength={100}
                      />
                      <Row>
                        <Col>
                          <Controller
                            name="tax_number"
                            control={control}
                            render={({ field }) => (
                              <FieldGroup label="Tax Identification Number" required errorMessage={errors.tax_number?.message}>
                                <IMaskInput
                                  mask="000-`000-`000-`000"
                                  value={field.value}
                                  unmask={false}
                                  inputRef={field.ref}
                                  onAccept={(value) => {
                                    field.onChange(value);
                                  }}
                                  placeholder="123-456-789-000"
                                  className="field"
                                />
                              </FieldGroup>
                            )}
                          />
                        </Col>
                        <Col>
                          <Controller
                            name="tax_category"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                label="Tax Category"
                                placeholder="Select Tax Category"
                                required
                                options={tax_categories}
                                getOptionLabel={(option) => option.label}
                                getOptionValue={(option) => option.value}
                                onChange={(newOption) => {
                                  field.onChange(newOption?.value);
                                }}
                                value={tax_categories?.find((tax_category) => tax_category.value == field.value) || null}
                                errorMessage={errors.tax_category?.message}
                              />
                            )}
                          />
                        </Col>
                        <Col sm={3}>
                          <Controller
                            name="withholding_tax_rate"
                            control={control}
                            render={({ field }) => (
                              <FieldGroup label="Withholding Tax Rate" required errorMessage={errors.withholding_tax_rate?.message}>
                                <IMaskInput
                                  mask="0.00"
                                  padFractionalZeros={true}
                                  value={field.value?.toString()}
                                  unmask={false}
                                  inputRef={field.ref}
                                  onAccept={(value) => {
                                    field.onChange(value);
                                  }}
                                  className="field"
                                  placeholder="0.01"
                                  min={0}
                                  max={9999999}
                                />
                              </FieldGroup>
                            )}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Input
                            {...register("email")}
                            label="Email Address"
                            placeholder="johndoe@gmail.com"
                            errorMessage={errors.email?.message}
                            maxLength={100}
                          />
                        </Col>
                        <Col>
                          <Controller
                            name="contact_number"
                            control={control}
                            render={({ field }) => (
                              <FieldGroup label="Contact Number">
                                <IMaskInput
                                  mask="+63 000 000 0000"
                                  value={field.value}
                                  unmask={false}
                                  inputRef={field.ref}
                                  onAccept={(value) => {
                                    field.onChange(value);
                                  }}
                                  placeholder="+63 999 999 9999"
                                  className="field"
                                />
                              </FieldGroup>
                            )}
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col>
                      <Input
                        {...register("address")}
                        label="Address"
                        required
                        placeholder="Angeles, Pampanga"
                        errorMessage={errors.address?.message}
                        maxLength={100}
                      />
                      <Controller
                        name="account_id"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            label="Default GL Account"
                            placeholder="Select GL Account"
                            required
                            options={accounts}
                            getOptionLabel={(option) => `${option.code} - ${option.name}`}
                            getOptionValue={(option) => option.id}
                            isOptionDisabled={(option) => !option.active}
                            noOptionsMessage={() => "No GL Accounts defined."}
                            onChange={(newOption) => {
                              field.onChange(newOption?.id);
                            }}
                            value={accounts.find((account) => account.id == field.value) || null}
                            errorMessage={errors.account_id?.message}
                          />
                        )}
                      />
                      <Controller
                        name="remarks"
                        control={control}
                        render={({ field }) => (
                          <FieldGroup label="Remarks" errorMessage={errors.remarks?.message}>
                            <textarea {...field} className="field" placeholder="Remarks" maxLength={255} value={field.value} />
                          </FieldGroup>
                        )}
                      />
                    </Col>
                  </Row>
                </Container>
              </Card.Body>
            </TabPanel>
            {supplier.id && (
              <TabPanel>
                <SupplierItemsForm supplier={supplier} inventory_items={inventory_items} warehouses={warehouses} credit_terms={credit_terms} />
              </TabPanel>
            )}
          </Tabs>
        </Form>
      </FormProvider>
    </React.StrictMode>
  );
};

export default SupplierForm;
